import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnterweisungenLayout } from './UnterweisungenLayout';
import { Card, CardContent } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronRight, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const UnterweisungenKategorien = () => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const instructionsRes = await axios.get(`${API}/api/instructions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setInstructions(instructionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Group instructions by category
  const instructionsByCategory = {};
  instructions.forEach(inst => {
    if (!instructionsByCategory[inst.category]) {
      instructionsByCategory[inst.category] = [];
    }
    instructionsByCategory[inst.category].push(inst);
  });

  // Get unique categories
  const uniqueCategories = Object.keys(instructionsByCategory).sort();

  const handleCategoryClick = (categoryName) => {
    navigate(`/admin/unterweisungen/kategorien/${encodeURIComponent(categoryName)}`);
  };

  if (loading) {
    return (
      <UnterweisungenLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </UnterweisungenLayout>
    );
  }

  return (
    <UnterweisungenLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Kategorien</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {uniqueCategories.length} Kategorie{uniqueCategories.length !== 1 ? 'n' : ''}
          </p>
        </div>

        {/* Categories */}
        {uniqueCategories.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
              <div className="p-4 bg-purple-100 rounded-full mb-4">
                <FileText className="h-12 w-12 text-purple-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Keine Kategorien</h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 text-center max-w-md">
                Erstellen Sie Unterweisungen, um Kategorien zu sehen
              </p>
            </CardContent>
          </Card>
        ) : (
          <div>
            {/* Desktop: Grid of Cards */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {uniqueCategories.map(categoryName => {
                const categoryInstructions = instructionsByCategory[categoryName];
                const count = categoryInstructions.length;
                
                return (
                  <button
                    key={categoryName}
                    onClick={() => handleCategoryClick(categoryName)}
                    className="text-left"
                  >
                    <Card className="hover:shadow-lg transition-shadow h-full">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-purple-100 rounded-lg flex-shrink-0">
                            <div className="text-2xl">📚</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                              {categoryName}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {count} Unterweisung{count !== 1 ? 'en' : ''}
                            </p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>

            {/* Mobile: List */}
            <div className="sm:hidden space-y-2">
              {uniqueCategories.map(categoryName => {
                const categoryInstructions = instructionsByCategory[categoryName];
                const count = categoryInstructions.length;
                
                return (
                  <button
                    key={categoryName}
                    onClick={() => handleCategoryClick(categoryName)}
                    className="w-full text-left"
                  >
                    <Card className="hover:bg-gray-50 dark:bg-gray-800 transition-colors">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          {/* Icon */}
                          <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                            <div className="text-xl">📚</div>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                              {categoryName}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {count} Unterweisung{count !== 1 ? 'en' : ''}
                            </p>
                          </div>
                          
                          {/* Arrow */}
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </UnterweisungenLayout>
  );
};
